import styled from '@emotion/styled'

export interface SwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  /** Accessible label; required since the control is icon-only. */
  label: string
  disabled?: boolean
  id?: string
}

// Switch is an accessible on/off toggle (role=switch) for boolean settings like
// a lesson's free-preview flag or a course's publish state (KAT-294/297). It is
// a real button so it's keyboard-operable and announced correctly.
export function Switch({ checked, onChange, label, disabled, id }: SwitchProps) {
  return (
    <Track
      type="button"
      role="switch"
      id={id}
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      data-on={checked}
      onClick={() => onChange(!checked)}
    >
      <Thumb data-on={checked} aria-hidden />
    </Track>
  )
}

const Track = styled.button`
  position: relative;
  width: 42px;
  height: 24px;
  border-radius: 999px;
  border: 1px solid var(--c-border-strong);
  background: var(--c-bg-sunk);
  cursor: pointer;
  padding: 0;
  transition: background var(--d-fast) var(--ease-standard), border-color var(--d-fast) var(--ease-standard);

  &[data-on='true'] {
    background: var(--c-accent);
    border-color: var(--c-accent);
  }
  &:focus-visible {
    outline: 2px solid var(--c-accent);
    outline-offset: 2px;
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const Thumb = styled.span`
  position: absolute;
  top: 2px;
  left: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  transition: transform var(--d-fast) var(--ease-emphasized);

  &[data-on='true'] {
    transform: translateX(18px);
  }
`
